import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Cpu, Activity, Bell, RefreshCw, LogOut, BookOpen, Menu, X } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { apiGet } from '../api/client';
export function AdminShell() {
  const {
    user,
    token,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [activeAlertsCount, setActiveAlertsCount] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  useEffect(() => {
    if (!token) return;
    apiGet('/admin/alerts/all?status=active&limit=1', token).then(res => {
      setActiveAlertsCount(res.total || 0);
    }).catch(err => {
      console.error('Failed to fetch active alerts count:', err);
      setActiveAlertsCount(0);
    });
  }, [token]);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const navItems = [{
    icon: LayoutDashboard,
    label: 'Dashboard',
    path: '/dashboard'
  }, {
    icon: Users,
    label: 'Users',
    path: '/users'
  }, {
    icon: Cpu,
    label: 'Devices',
    path: '/devices'
  }, {
    icon: Activity,
    label: 'Sessions',
    path: '/sessions'
  }, {
    icon: Bell,
    label: 'Alerts',
    path: '/alerts',
    badge: activeAlertsCount
  }, {
    icon: BookOpen,
    label: 'Content',
    path: '/content'
  }, {
    icon: RefreshCw,
    label: 'Sync Logs',
    path: '/sync-logs'
  }];
  return <div className="min-h-screen bg-background flex flex-col lg:flex-row" data-id="element-31">
      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-background border-b border-border flex items-center justify-between px-4 z-30" data-id="element-32">
        <div className="flex items-center gap-3" data-id="element-33">
          <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center" data-id="element-34">
            <div className="w-3 h-3 bg-white rounded-full" data-id="element-35" />
          </div>
          <span className="text-[16px] font-semibold text-white" data-id="element-36">
            Aurora Care Admin
          </span>
        </div>
        <button onClick={() => setIsSidebarOpen(true)} className="text-text-secondary hover:text-white transition-colors p-2" data-id="element-37">
          <Menu className="w-6 h-6" data-id="element-38" />
        </button>
      </div>

      {/* Sidebar Backdrop */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-39" />}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-[260px] lg:w-[220px] bg-background border-r border-border flex flex-col transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-40">
        <div className="p-6" data-id="element-41">
          <div className="flex items-center justify-between mb-6" data-id="element-42">
            <div className="flex items-center gap-3" data-id="element-43">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center" data-id="element-44">
                <div className="w-3 h-3 bg-white rounded-full" data-id="element-45" />
              </div>
              <span className="text-[16px] font-semibold text-white" data-id="element-46">
                Aurora Care
              </span>
            </div>
            <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden text-text-secondary hover:text-white transition-colors" data-id="element-47">
              <X className="w-5 h-5" data-id="element-48" />
            </button>
          </div>
          <div className="h-[1px] bg-border w-full mb-6" data-id="element-49" />

          <nav className="flex flex-col gap-[2px]" data-id="element-50">
            {navItems.map(item => <NavLink key={item.path} to={item.path} onClick={() => setIsSidebarOpen(false)} className={({
            isActive
          }) => `
                  w-full h-[36px] rounded-btn pl-3 pr-3 flex items-center gap-[10px] transition-colors duration-150
                  ${isActive ? 'bg-[rgba(107,33,232,0.15)] text-accent border border-[rgba(107,33,232,0.3)]' : 'bg-transparent text-text-secondary hover:bg-surface border border-transparent'}
                `} data-id="element-51">
                <item.icon className="w-4 h-4" data-id="element-52" />
                <span className="text-[14px] font-medium flex-1" data-id="element-53">
                  {item.label}
                </span>
                {item.badge && item.badge > 0 ? <span className="bg-danger text-white text-[10px] font-bold px-1.5 py-0.5 rounded-badge min-w-[20px] text-center" data-id="element-54">
                    {item.badge}
                  </span> : null}
              </NavLink>)}
          </nav>
        </div>

        <div className="mt-auto p-4 border-t border-border" data-id="element-55">
          <div className="flex items-center gap-3" data-id="element-56">
            <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold flex-shrink-0" data-id="element-57">
              {user?.email?.charAt(0).toUpperCase() || 'A'}
            </div>
            <span className="text-[13px] text-text-secondary truncate flex-1" data-id="element-58">
              {user?.email || 'admin@auroracare.com'}
            </span>
            <button onClick={handleLogout} className="text-text-tertiary hover:text-white transition-colors flex-shrink-0" title="Log out" data-id="element-59">
              <LogOut className="w-4 h-4" data-id="element-60" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-[220px] bg-background min-h-screen pt-16 lg:pt-0 w-full overflow-x-hidden" data-id="element-61">
        <div className="p-4 md:p-6 lg:p-8" data-id="element-62">
          <Outlet data-id="element-63" />
        </div>
      </main>
    </div>;
}